import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { type OptionItem } from "@client/controls/Inputs/Select/component";
import { apiAction } from "@client/lib/actions";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import appInfo from "@common/appInfo";
import toasts from "@common/lib/toast";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type MemberWallet } from "./types";

export enum Actions {
  InitBegin = "MEMBER_WALLET_INIT_BEGIN",
  InitFailure = "MEMBER_WALLET_INIT_FAILURE",
  InitSuccess = "MEMBER_WALLET_INIT_SUCCESS",

  CreateAccountBegin = "MEMBER_WALLET_CREATE_ACCOUNT_BEGIN",
  CreateAccountFailure = "MEMBER_WALLET_CREATE_ACCOUNT_FAILURE",
  CreateAccountSuccess = "MEMBER_WALLET_CREATE_ACCOUNT_SUCCESS",

  UpdateAccountBegin = "MEMBER_WALLET_UPDATE_ACCOUNT_BEGIN",
  UpdateAccountFailure = "MEMBER_WALLET_UPDATE_ACCOUNT_FAILURE",
  UpdateAccountSuccess = "MEMBER_WALLET_UPDATE_ACCOUNT_SUCCESS",

  SetShowingAccountDetailId = "MEMBER_WALLET_SET_SHOWING_ACCOUNT_DETAIL_ID",

  OnTransferred = "MEMBER_WALLET_ON_TRANSFERRED",

  SetAddEditAccountModalVisible = "MEMBER_WALLET_SHOW_ADD_EDIT_ACCOUNT_MODAL",
  SetTransferModalVisible = "MEMBER_WALLET_SHOW_TRANSFER_MODAL"
}

export type InitBeginActionData = EntityRef;
export interface InitSuccessActionData {
  accounts: MemberWallet.Account[];
  currencies: MemberWallet.Currency[];
  monthOptions: OptionItem<string>[];
}
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  MemberWallet.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return appInfo.user?.id
      ? api.init(apiInfo)
      : Promise.resolve<HttpApiResponse<MemberWallet.Api.InitResponse>>({
          status: 200,
          response: {
            status: "success",
            data: {
              accounts: []
            }
          }
        });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type WithItemId = {
  accountId: string;
};

export type CreateAccountBeginActionData = EntityRef & {
  data: MemberWallet.View.Account;
};
export type CreateUpdateAccountSuccessActionData = MemberWallet.Api.CreateUpdateAccountResponse;
export const _createAccount = apiAction<
  BaseAppState,
  CreateAccountBeginActionData,
  MemberWallet.Api.CreateUpdateAccountResponse,
  CreateUpdateAccountSuccessActionData
>({
  types: {
    begin: Actions.CreateAccountBegin,
    failure: Actions.CreateAccountFailure,
    success: [
      Actions.CreateAccountSuccess,
      ({ dispatch, params }) => dispatch(hideAddEditAccountModal(params)),
      () => {
        toasts.submitted();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CreateAccountBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.createAccount(apiInfo, params.data);
  }
});

export const updateAccount =
  (ref: EntityRef, data: MemberWallet.View.Account) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Updating") {
      dispatch(_updateAccount({ ...ref, data: { ...data, accountId: localState.showingAccountDetailId! } }));
    }
  };

export type UpdateAccountBeginActionData = EntityRef & {
  data: MemberWallet.View.Account;
};
export const _updateAccount = apiAction<
  BaseAppState,
  UpdateAccountBeginActionData,
  MemberWallet.Api.CreateUpdateAccountResponse,
  CreateUpdateAccountSuccessActionData
>({
  types: {
    begin: Actions.UpdateAccountBegin,
    failure: Actions.UpdateAccountFailure,
    success: [
      Actions.UpdateAccountSuccess,
      ({ dispatch, params }) => dispatch(hideAddEditAccountModal(params)),
      () => {
        toasts.submitted();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: UpdateAccountBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.updateAccount(apiInfo, params.data);
  }
});

export const createAccount =
  (ref: EntityRef, data: MemberWallet.View.Account) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Creating") {
      dispatch(_createAccount({ ...ref, data }));
    }
  };

export type SetShowingAccountDetailIdActionData = EntityRef & {
  accountId: string | null;
};
export const setShowingAccountDetailId = (args: SetShowingAccountDetailIdActionData) => (dispatch: Dispatch<any>) => {
  dispatch({
    type: Actions.SetShowingAccountDetailId,
    data: <SetShowingAccountDetailIdActionData>args
  });
};

export type SetModalVisibleActionData = EntityRef & {
  isVisible: boolean;
};
export const hideAddEditAccountModal = (ref: EntityRef) => ({
  type: Actions.SetAddEditAccountModalVisible,
  data: <SetModalVisibleActionData>{ ...ref, isVisible: false }
});
export const showAddEditAccountModal = (ref: EntityRef) => ({
  type: Actions.SetAddEditAccountModalVisible,
  data: <SetModalVisibleActionData>{ ...ref, isVisible: true }
});

export type OnTransferredActionData = EntityRef & {
  accounts: MemberWallet.Account[];
};
export const onTransferred = (ref: EntityRef, accounts: MemberWallet.Account[]) => ({
  type: Actions.OnTransferred,
  data: {
    ...ref,
    accounts
  }
});

export const hideTransferModal = (ref: EntityRef) => ({
  type: Actions.SetTransferModalVisible,
  data: <SetModalVisibleActionData>{ ...ref, isVisible: false }
});
export const showTransferModal = (ref: EntityRef) => ({
  type: Actions.SetTransferModalVisible,
  data: <SetModalVisibleActionData>{ ...ref, isVisible: true }
});

export type SetConfirmationTransferModalVisibleActionData = EntityRef & {
  isVisible: boolean;
  data: MemberWallet.TransferData | null;
};
